<template>
    <NuxtLayout>
        <NuxtPage />
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {onBeforeMount, onBeforeUnmount, provide, ref} from 'vue';
    import type {HostSettingsResource} from '#imports';
    import {storeToRefs, useAsyncData, useAuth, useHostSettings, useLocale, usePrismicClient, usePrismicPreviewScript} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import type {ResourcesCenterHeaderDocument} from '@malt/nuxt-prismic-module/prismicio-types';
    import {usePageCache} from '@malt/utils';
    import {HOST_SETTINGS, MEDIA_BREAKPOINTS, PRISMIC_CLIENT, RC_NAVBAR, USER_LOCALE} from '~/app/keys';
    import usePrismicHeader from '~/composables/content/usePrismicHeader';
    import {useBreakpointsHelper} from '~/composables/useBreakpointsHelpers';

    const enablePageCache = useFeatureFlag('enable-page-cache');
    const authStore = useAuth();
    const {loggedIn} = storeToRefs(authStore);

    usePrismicPreviewScript();

    const {locale} = useLocale();
    provide(USER_LOCALE, locale.value);

    const breakpoints = ref(useBreakpointsHelper());
    provide(MEDIA_BREAKPOINTS, breakpoints);

    const client = usePrismicClient();
    provide(PRISMIC_CLIENT, client);

    const {data: navbarDocument} = await useAsyncData('navbar', () => usePrismicHeader(client));
    provide(RC_NAVBAR, navbarDocument.value as ResourcesCenterHeaderDocument);

    function handleResize() {
        breakpoints.value = useBreakpointsHelper();
    }
    const {data: hostSettings} = await useAsyncData('host-settings', () => useHostSettings());
    provide(HOST_SETTINGS, hostSettings.value as HostSettingsResource);

    if (!loggedIn.value && enablePageCache.value) {
        usePageCache();
    }

    onBeforeMount(() => {
        window.addEventListener('resize', handleResize);
    });
    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize);
    });
</script>
